import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Label,
} from "@devhop/ui";
import { ChevronDownIcon, LandmarkIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  countPaymentTermsItems,
  paymentTermsHtmlToNullable,
  toPaymentTermsEditorHtml,
} from "@/shared/lib/performance-document-payment-terms";
import { SimpleRichTextEditor } from "@/shared/ui/SimpleRichTextEditor";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import type { DocumentDTO } from "../../domain/contracts";
import { taxEnabledToVariant } from "../../domain/contracts/payment-terms-templates";
import {
  useBankAccountTemplatesLookup,
  useUpdateDocumentBankAccount,
} from "../api/queries";

type DocumentBankAccountEditorProps = {
  eventId: string;
  document: DocumentDTO;
  canEdit: boolean;
};

export function DocumentBankAccountEditor({
  eventId,
  document,
  canEdit,
}: DocumentBankAccountEditorProps) {
  const taxVariant = taxEnabledToVariant(document.taxEnabled ?? false);
  const templatesQuery = useBankAccountTemplatesLookup(taxVariant);
  const updateBankAccount = useUpdateDocumentBankAccount(eventId, document.id);
  const [contentLo, setContentLo] = useState(() =>
    toPaymentTermsEditorHtml(document.bankAccountInfo),
  );
  const [contentEn, setContentEn] = useState(() =>
    toPaymentTermsEditorHtml(document.bankAccountInfoEn),
  );
  const [selectedTemplateId, setSelectedTemplateId] = useState("");

  useEffect(() => {
    setContentLo(toPaymentTermsEditorHtml(document.bankAccountInfo));
    setContentEn(toPaymentTermsEditorHtml(document.bankAccountInfoEn));
    setSelectedTemplateId("");
  }, [
    document.id,
    document.bankAccountInfo,
    document.bankAccountInfoEn,
    document.taxEnabled,
  ]);

  const hydratedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!canEdit || document.documentType !== "invoice") return;
    if (document.bankAccountInfo?.trim() || document.bankAccountInfoEn?.trim()) {
      return;
    }
    if (templatesQuery.isLoading || updateBankAccount.isPending) return;

    const hydrateKey = `${document.id}:${document.taxEnabled ? "tax" : "notax"}`;
    if (hydratedKeyRef.current === hydrateKey) return;

    const defaultTemplate = (templatesQuery.data?.items ?? []).find(
      (item) => item.isDefault,
    );
    if (!defaultTemplate?.content?.trim() && !defaultTemplate?.contentEn?.trim()) {
      return;
    }

    hydratedKeyRef.current = hydrateKey;
    const htmlLo = toPaymentTermsEditorHtml(defaultTemplate.content);
    const htmlEn = toPaymentTermsEditorHtml(defaultTemplate.contentEn);
    setSelectedTemplateId(defaultTemplate.id);
    setContentLo(htmlLo);
    setContentEn(htmlEn);
    updateBankAccount.mutate({
      bankAccountInfo: paymentTermsHtmlToNullable(htmlLo),
      bankAccountInfoEn: paymentTermsHtmlToNullable(htmlEn),
    });
  }, [
    canEdit,
    document.documentType,
    document.bankAccountInfo,
    document.bankAccountInfoEn,
    document.id,
    document.taxEnabled,
    templatesQuery.data?.items,
    templatesQuery.isLoading,
    updateBankAccount.isPending,
    updateBankAccount.mutate,
  ]);

  const savedItemCount = useMemo(
    () => countPaymentTermsItems(document.bankAccountInfo),
    [document.bankAccountInfo],
  );

  if (!canEdit || document.documentType !== "invoice") return null;

  const templates = templatesQuery.data?.items ?? [];
  const savedHtmlLo = toPaymentTermsEditorHtml(document.bankAccountInfo);
  const savedHtmlEn = toPaymentTermsEditorHtml(document.bankAccountInfoEn);
  const dirty =
    paymentTermsHtmlToNullable(contentLo) !==
      paymentTermsHtmlToNullable(savedHtmlLo) ||
    paymentTermsHtmlToNullable(contentEn) !==
      paymentTermsHtmlToNullable(savedHtmlEn);
  const templateOptions = templates.map((template) => ({
    value: template.id,
    label: `${template.name}${template.isDefault ? " (ເລີ່ມຕົ້ນ)" : ""}`,
  }));
  const taxLabel = taxVariant === "with_tax" ? "ເອກະສານມີພາສີ" : "ເອກະສານບໍ່ມີພາສີ";

  const applyTemplate = (template: (typeof templates)[number]) => {
    setContentLo(toPaymentTermsEditorHtml(template.content));
    setContentEn(toPaymentTermsEditorHtml(template.contentEn));
  };

  return (
    <Collapsible defaultOpen={false} className="group/collapsible">
      <div className="rounded-xl border bg-card">
        <CollapsibleTrigger className="w-full text-left">
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 hover:bg-muted/30">
            <div className="flex min-w-0 items-center gap-2">
              <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground transition-transform duration-200 group-data-[state=open]/collapsible:rotate-180" />
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <LandmarkIcon className="size-4" />
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-sm">ຂໍ້ມູນບັນຊີທະນາຄານ</p>
                <p className="text-muted-foreground text-xs">
                  ລາວ + English · ແມ່ແບບຕາມ {taxLabel}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 pl-6 sm:pl-0">
              {dirty ? (
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-amber-900 text-xs dark:bg-amber-950/40 dark:text-amber-100">
                  ຍັງບໍ່ບັນທຶກ
                </span>
              ) : null}
              <span className="text-muted-foreground text-xs">
                {savedItemCount > 0 ? `${savedItemCount} ຂໍ້ (ລາວ)` : "ຍັງບໍ່ມີຂໍ້ມູນບັນຊີ"}
              </span>
            </div>
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="space-y-4 border-t px-4 py-4">
            <p className="text-muted-foreground text-xs">
              ເມື່ອເປີດ/ປິດພາສີ ລະບົບຈະໂຫຼດແມ່ແບບເລີ່ມຕົ້ນທີ່ກົງກັນ (ຖ້າຍັງບໍ່ໄດ້ແກ້ເນື້ອຫາເອງ)
            </p>

            <div className="grid gap-3 md:grid-cols-[220px_1fr] md:items-end">
              <div className="space-y-1.5">
                <Label>ໃຊ້ແມ່ແບບ ({taxLabel})</Label>
                <SimpleSelect
                  value={selectedTemplateId || undefined}
                  onValueChange={(id) => {
                    if (!id) {
                      setSelectedTemplateId("");
                      return;
                    }
                    setSelectedTemplateId(id);
                    const template = templates.find((item) => item.id === id);
                    if (template) applyTemplate(template);
                  }}
                  placeholder="— ເລືອກແມ່ແບບ —"
                  options={templateOptions}
                  className="w-full"
                  clearable
                  disabled={
                    templatesQuery.isLoading || templateOptions.length === 0
                  }
                />
              </div>
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={updateBankAccount.isPending}
                  onClick={() => {
                    const defaultTemplate = templates.find(
                      (item) => item.isDefault,
                    );
                    if (defaultTemplate) {
                      setSelectedTemplateId(defaultTemplate.id);
                      applyTemplate(defaultTemplate);
                    }
                  }}
                >
                  ໂຫຼດຄ່າເລີ່ມຕົ້ນ
                </Button>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>ຂໍ້ມູນບັນຊີ (ລາວ)</Label>
              <SimpleRichTextEditor
                resetKey={`${document.id}:${document.taxEnabled ? "tax" : "notax"}:bank-lo:${selectedTemplateId || "custom"}`}
                initialHtml={contentLo}
                onHtmlChange={setContentLo}
                disabled={updateBankAccount.isPending}
                placeholder="ຂຽນຄຳອະທິບາຍບລັອກນີ້…"
                minHeightClassName="min-h-[160px]"
              />
            </div>

            <div className="space-y-1.5">
              <Label>Bank account (English)</Label>
              <SimpleRichTextEditor
                resetKey={`${document.id}:${document.taxEnabled ? "tax" : "notax"}:bank-en:${selectedTemplateId || "custom"}`}
                initialHtml={contentEn}
                onHtmlChange={setContentEn}
                disabled={updateBankAccount.isPending}
                placeholder="Write bank account details in English…"
                minHeightClassName="min-h-[140px]"
              />
            </div>

            <div className="flex justify-end">
              <Button
                type="button"
                disabled={!dirty || updateBankAccount.isPending}
                onClick={() =>
                  updateBankAccount.mutate({
                    bankAccountInfo: paymentTermsHtmlToNullable(contentLo),
                    bankAccountInfoEn: paymentTermsHtmlToNullable(contentEn),
                  })
                }
              >
                {updateBankAccount.isPending ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກຂໍ້ມູນບັນຊີ"}
              </Button>
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}
